import classNames from 'classnames'
import React, { useState, useEffect } from 'react'
import { TitleController, ButtonGroup, Loading } from '@pandiafi/design-system'
import { useTranslation } from 'react-i18next'
import {
  ResidentAppBackLink,
  ResidentAppCard,
  ResidentAppView,
} from '@pandiafi/components'

import { ReservationCalendarsDataLoader } from '../ReservationCalendars/ReservationCalendarsDataLoader'
import { ReservationCalendars } from '../ReservationCalendars'
import { ReservationsList } from '../../components/ReservationsList'
import { useAPIGetReservations } from '../../api/apiHooks/useAPIGetReservations'
import type { ReservationCalendarsResponse } from '../../../common/validations/reservationCalendarSchemas'
import { useCurrentContract } from '../../hooks/useCurrentContract/useCurrentContract'
import { useModuleConfig } from '../../hooks/useModuleConfig/useModuleConfig'

import { ReservationRulesAndTermsLinks } from 'components'
import { EmptyState } from 'components/EmptyState/EmptyState'
import { useContractPaths } from 'hooks/useContractPaths/useContractPaths'
import { useIsPhoneLayout } from 'hooks/useIsPhoneLayout/useIsPhoneLayout'

type ReservationCalendarForResident = ReservationCalendarsResponse[0]

const LaundryReservationsContent: React.FC<{
  calendars: ReservationCalendarForResident[]
  isLoading: boolean
}> = ({ calendars, isLoading }) => {
  const { t } = useTranslation(['common', 'titles', 'reservations'])
  const { frontPagePath } = useContractPaths()
  const isPhone = useIsPhoneLayout()
  const [equipmentFilter, setEquipmentFilter] = useState<string | undefined>()
  const { currentContractId } = useCurrentContract()
  const { data: reservations = [], isLoading: reservationsLoading } =
    useAPIGetReservations(currentContractId)
  const moduleConfig = useModuleConfig()

  const laundryConfig = moduleConfig.laundryReservation
  const laundryFallbackLinks: Array<{ href: string; label: string }> = []
  if (laundryConfig?.contractDocumentLink) {
    laundryFallbackLinks.push({
      href: laundryConfig.contractDocumentLink,
      label: t('reservations:modal.commonAreaRulesLink'),
    })
  }
  if (laundryConfig?.termsOfUseDocumentLink) {
    laundryFallbackLinks.push({
      href: laundryConfig.termsOfUseDocumentLink,
      label: t('reservations:modal.commonAreaTermsLink'),
    })
  }

  const allEquipmentFilters = [
    {
      id: 'laundry_room',
      label: t('reservations:filters.laundryRoom'),
    },
    {
      id: 'washing_machine,allergy_washing_machine',
      label: t('reservations:filters.washingMachines'),
    },
    { id: 'dryer', label: t('reservations:filters.dryers') },
    { id: 'mangle', label: t('reservations:filters.mangles') },
  ]

  const availableCategories = new Set(
    calendars.map((cal) => cal.reservableCategory)
  )
  const equipmentFilters = allEquipmentFilters.filter((filter) => {
    const ids = filter.id.split(',')
    return ids.some((id) => availableCategories.has(id))
  })

  const allViewCategories = allEquipmentFilters
    .map((filter) => filter.id)
    .join(',')
  // On phones the list sits between the filters and the calendar, on larger
  // screens in the aside. One instance, so it fetches and renders once.
  const reservationsList = (
    <ReservationsList reservableCategory={allViewCategories} />
  )

  const categories = new Set(allViewCategories.split(','))
  const hasLaundryReservations = reservations.some((reservation) =>
    categories.has(reservation.reservableCategory)
  )

  useEffect(() => {
    if (equipmentFilters.length > 0) {
      const currentFilterExists = equipmentFilters.find(
        (f) => f.id === equipmentFilter
      )
      if (!currentFilterExists) {
        setEquipmentFilter(equipmentFilters[0].id)
      }
    }
  }, [equipmentFilters, equipmentFilter])

  const loading = isLoading || reservationsLoading
  const hasReservableEquipment = equipmentFilters.length > 0

  return (
    <ResidentAppView
      title={t('titles:laundryReservations', 'Reservations')}
      backLink={
        <ResidentAppBackLink href={frontPagePath}>
          {t('common:views.home')}
        </ResidentAppBackLink>
      }
      aside={
        <>
          <ReservationRulesAndTermsLinks
            contractId={currentContractId}
            reservableCategory="washing_machine"
            realPropertyHierarchyId={null}
            fallbackLinks={laundryFallbackLinks}
          />
          {loading ? (
            <div className="flex justify-center py-4">
              <Loading variant="details" />
            </div>
          ) : !isPhone && (hasReservableEquipment || hasLaundryReservations) ? (
            reservationsList
          ) : null}
        </>
      }
    >
      <ResidentAppCard>
        {loading ? (
          <div className="flex justify-center items-center py-12">
            <Loading variant="details" />
          </div>
        ) : hasReservableEquipment ? (
          <div className="space-y-12">
            <div>
              <div className="overflow-hidden rounded-lg min-tablet:hidden">
                {equipmentFilters.map((option, index) => {
                  const isSelected = equipmentFilter === option.id
                  const isFirst = index === 0
                  const isLast = index === equipmentFilters.length - 1
                  const previousIsSelected =
                    equipmentFilters[index - 1]?.id === equipmentFilter
                  const nextIsSelected =
                    equipmentFilters[index + 1]?.id === equipmentFilter

                  const rowClassName = classNames({
                    'border-t-2 border-common-gray-7':
                      !isFirst && !isSelected && !previousIsSelected,
                    'relative z-10': isSelected,
                  })

                  const buttonClassName = classNames(
                    'w-full border-x-2 px-4 py-2.5 text-center text-base font-medium',
                    {
                      'rounded-t-lg border-t-2': isFirst,
                      'rounded-b-lg border-b-2': isLast,
                      '-my-[2px] border-2 border-brand-6 bg-brand-6 text-white':
                        isSelected,
                      'border-common-gray-7 bg-white text-common-gray-7':
                        !isSelected,
                      'border-b-0': !isLast && !isSelected && !nextIsSelected,
                    }
                  )

                  return (
                    <div key={option.id} className={rowClassName}>
                      <button
                        type="button"
                        onClick={() => setEquipmentFilter(option.id)}
                        className={buttonClassName}
                      >
                        {option.label}
                      </button>
                    </div>
                  )
                })}
              </div>

              <div className="hidden min-tablet:block">
                <ButtonGroup
                  suggestions={equipmentFilters}
                  value={equipmentFilter}
                  onChange={(value) => setEquipmentFilter(value)}
                  fullwidth
                  sameSize
                  hasIcon={false}
                />
              </div>
            </div>
            {isPhone && reservationsList}
            <ReservationCalendars reservableCategory={equipmentFilter} />
          </div>
        ) : (
          <div className="space-y-12">
            {isPhone && reservationsList}
            <EmptyState
              headingLevel={2}
              title={t('reservations:noReservableEquipment')}
              illustration="house-missing"
            />
          </div>
        )}
      </ResidentAppCard>
    </ResidentAppView>
  )
}

export const LaundryReservations: React.FC = () => {
  const { t } = useTranslation(['titles', 'reservations'])

  return (
    <TitleController title={t('titles:laundryReservations', 'Reservations')}>
      <ReservationCalendarsDataLoader>
        {(calendars: ReservationCalendarForResident[], isLoading: boolean) => (
          <LaundryReservationsContent
            calendars={calendars}
            isLoading={isLoading}
          />
        )}
      </ReservationCalendarsDataLoader>
    </TitleController>
  )
}
